/**
 * @fileoverview DICOM Download Handler
 * Handles both direct downloads (200) and async job downloads (202) with polling
 */

import { apiV3 } from "../api/index";
import { downloadJobManager } from "./download-job-manager";
import { DOWNLOAD_CONFIG } from "../config/download-config";
import { fetchZipFile } from "../services/downloadService";

/**
 * Check if a job status is considered "pending" (includes both "pending" and "processing")
 */
export function isPendingStatus(status) {
  return status === "pending" || status === "processing";
}

/**
 * Helper function to check if response is a job response (202)
 */
export function isJobDownloadResponse(response) {
  return response.status_code === 202;
}

/**
 * Helper function to check if response is a direct download response (200)
 */
export function isDirectDownloadResponse(response) {
  return response.status_code === 200;
}

/**
 * Download a file from a URL using fetchZipFile
 */
async function downloadFileFromUrl(
  url,
  filename,
  onProgress,
  downloadId
) {
  try {
    console.log(`[downloadFileFromUrl] Starting download - URL: ${url}, Filename: ${filename}, Download ID: ${downloadId || 'none'}`);
    const id = await fetchZipFile(url, filename, onProgress || (() => {}), downloadId);
    console.log(`[downloadFileFromUrl] Download completed successfully - ID: ${id}`);
    return id;
  } catch (error) {
    console.error(`[downloadFileFromUrl] Failed to download file - URL: ${url}, Error:`, error);
    throw error;
  }
}

/**
 * Poll job status until completed or max attempts reached
 */
async function pollJobStatus(jobId, onProgress) {
  let attempts = 0;

  while (attempts < DOWNLOAD_CONFIG.MAX_POLLING_ATTEMPTS) {
    attempts++;

    try {
      const response = await apiV3.get(`/dicomweb/jobs/${jobId}`);
      const jobData = response.data.data;

      // Update stored job with latest data
      downloadJobManager.updateJob(jobId, jobData);

      // Notify progress
      if (onProgress) {
        onProgress(jobData.status, attempts);
      }

      console.log(
        `Job ${jobId} status: ${jobData.status} (attempt ${attempts}/${DOWNLOAD_CONFIG.MAX_POLLING_ATTEMPTS})`
      );

      // Check if job is completed
      if (jobData.status === "completed") {
        if (!jobData.download_url) {
          throw new Error("Job completed but no download URL provided");
        }
        return jobData;
      }

      // Check if job failed
      if (jobData.status === "failed") {
        throw new Error(
          jobData.error || "Job failed without error message"
        );
      }

      // Wait before next poll (pending or processing status)
      await new Promise((resolve) =>
        setTimeout(resolve, DOWNLOAD_CONFIG.POLLING_INTERVAL)
      );
    } catch (error) {
      console.error(`Error polling job ${jobId}:`, error);

      // If we've exhausted attempts, throw the error
      if (attempts >= DOWNLOAD_CONFIG.MAX_POLLING_ATTEMPTS) {
        throw new Error(
          `Max polling attempts reached for job ${jobId}. Last error: ${error}`
        );
      }

      // Otherwise, wait and retry
      await new Promise((resolve) =>
        setTimeout(resolve, DOWNLOAD_CONFIG.POLLING_INTERVAL)
      );
    }
  }

  throw new Error(
    `Job ${jobId} did not complete within ${DOWNLOAD_CONFIG.MAX_POLLING_ATTEMPTS} attempts`
  );
}

/**
 * Handle download job - either direct or with polling
 */
async function handleDownloadJob(
  jobData,
  resourceType,
  filename,
  onProgress,
  onDownloadProgress,
  downloadId = null
) {
  // Store the job
  downloadJobManager.storeJob(jobData, resourceType, filename);

  try {
    // Use the provided downloadId if available, otherwise fall back to job_id
    const finalDownloadId = downloadId || jobData.job_id;
    console.log(`[handleDownloadJob] Using downloadId: ${finalDownloadId} (provided: ${downloadId}, job_id: ${jobData.job_id})`);

    // Check if already completed
    if (jobData.status === "completed" && jobData.download_url) {
      console.log("Job already completed, downloading directly");
      await downloadFileFromUrl(jobData.download_url, filename, onDownloadProgress, finalDownloadId);
      downloadJobManager.removeJob(jobData.job_id);
      return;
    }

    // Poll until completed
    console.log(`Polling job ${jobData.job_id}...`);
    const completedJob = await pollJobStatus(jobData.job_id, onProgress);

    // Download the file
    console.log(`[handleDownloadJob] Job completed, starting download - URL: ${completedJob.download_url}, Filename: ${filename}, DownloadId: ${finalDownloadId}`);
    await downloadFileFromUrl(
      completedJob.download_url,
      filename,
      onDownloadProgress,
      finalDownloadId // Use the provided downloadId instead of job_id to avoid duplicates
    );
    console.log(`[handleDownloadJob] File download completed for job ${jobData.job_id}`);
    
    // Ensure the download is marked as complete in the global manager
    // Also check for any downloads with matching filename or studyId
    try {
      const { completeDownload, getActiveDownloads } = require("../utils/download-manager");
      const allDownloads = getActiveDownloads();
      
      // Mark the download as complete using the finalDownloadId
      const jobDownload = allDownloads.find(d => d.id === finalDownloadId);
      if (jobDownload && jobDownload.status !== "completed") {
        console.log(`[handleDownloadJob] Marking download as completed: ${finalDownloadId}`);
        completeDownload(finalDownloadId, true);
      }
      
      // Also mark any downloads with matching filename or studyId as complete
      // This handles edge cases where there might be duplicate registrations
      const matchingDownloads = allDownloads.filter(d => 
        d.id !== finalDownloadId && 
        (d.filename === filename || d.studyId === jobData.study_uid) &&
        d.status === "downloading"
      );
      
      matchingDownloads.forEach(download => {
        console.log(`[handleDownloadJob] Marking matching download as completed: ${download.id} (filename: ${download.filename})`);
        completeDownload(download.id, true);
      });
    } catch (error) {
      console.warn("Failed to mark download as complete:", error);
    }

    // Clean up stored job
    downloadJobManager.removeJob(jobData.job_id);

    console.log(`Download completed for job ${jobData.job_id}`);
  } catch (error) {
    console.error(`Download failed for job ${jobData.job_id}:`, error);

    // Keep the job in storage for potential retry
    // User can recover it on next page load
    throw error;
  }
}

/**
 * Process download job in background (for 202 responses)
 */
export async function processDownloadJobInBackground(
  jobData,
  resourceType,
  filename,
  onJobReady,
  onJobFailed
) {
  // Store the job
  downloadJobManager.storeJob(jobData, resourceType, filename);

  // Process in background
  (async () => {
    try {
      // Check if already completed
      if (jobData.status === "completed" && jobData.download_url) {
        console.log("Job already completed, triggering download");
        if (onJobReady) {
          onJobReady(jobData.download_url, filename);
        }
        downloadJobManager.removeJob(jobData.job_id);
        return;
      }

      // Poll until completed
      console.log(`Polling job ${jobData.job_id} in background...`);
      const completedJob = await pollJobStatus(jobData.job_id);

      // Notify that job is ready
      if (completedJob.download_url && onJobReady) {
        onJobReady(completedJob.download_url, filename);
      }

      // Clean up stored job
      downloadJobManager.removeJob(jobData.job_id);

      console.log(`Background job ${jobData.job_id} completed`);
    } catch (error) {
      console.error(`Background job ${jobData.job_id} failed:`, error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (onJobFailed) {
        onJobFailed(errorMessage);
      }

      // Keep the job in storage for potential retry
    }
  })();
}

/**
 * Download a study with automatic handling of direct/job responses
 * For 202 responses, processes in background and notifies when ready
 */
export async function downloadStudyWithPolling(
  studyId,
  filename,
  onProgress,
  onDownloadProgress,
  processInBackground,
  onJobReady,
  onJobFailed,
  downloadId = null
) {
  try {
    const response = await apiV3.get(
      `/dicomweb/studies/${studyId}?zip_file=true`
    );
    const downloadFilename = filename || `study-${studyId}.zip`;

    const responseData = response.data;

    // Handle direct download (200)
    if (isDirectDownloadResponse(responseData)) {
      console.log("Study download ready immediately (200)");
      // Don't download here - let the UI handle it to avoid duplicate downloads
      // The UI will call fetchZipFile with progress tracking
      return {
        success: true,
        downloadUrl: responseData.data.download_url,
      };
    }

    // Handle job response (202)
    if (isJobDownloadResponse(responseData)) {
      console.log("Study download initiated as job (202)");
      const jobData = responseData.data;

      if (processInBackground) {
        // Process in background - don't block UI
        processDownloadJobInBackground(
          jobData,
          "study",
          downloadFilename,
          onJobReady,
          onJobFailed
        );

        return {
          success: true,
          jobId: jobData.job_id,
        };
      } else {
        // Blocking mode - wait for completion
        await handleDownloadJob(
          jobData,
          "study",
          downloadFilename,
          onProgress,
          onDownloadProgress,
          downloadId // Pass the downloadId to use the same registration
        );

        return {
          success: true,
          jobId: jobData.job_id,
        };
      }
    }

    throw new Error("Unexpected response format");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("Failed to download study:", errorMessage);

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Resume a pending download job (called on page load)
 */
export async function resumeDownloadJob(
  storedJob,
  onProgress,
  onDownloadProgress
) {
  try {
    console.log(`Resuming download job: ${storedJob.job_id}`);

    // Check if we should continue polling
    if (!downloadJobManager.shouldContinuePolling(storedJob)) {
      console.warn(`Job ${storedJob.job_id} exceeded max polling attempts`);
      return {
        success: false,
        error: "Max polling attempts exceeded",
        jobId: storedJob.job_id,
      };
    }

    // Check if already completed
    if (storedJob.status === "completed" && storedJob.download_url) {
      const filename =
        storedJob.filename ||
        `${storedJob.resource_type}-${storedJob.job_id}.zip`;
      await downloadFileFromUrl(storedJob.download_url, filename, onDownloadProgress, storedJob.job_id);
      downloadJobManager.removeJob(storedJob.job_id);

      return {
        success: true,
        downloadUrl: storedJob.download_url,
        jobId: storedJob.job_id,
      };
    }

    // Continue polling
    const completedJob = await pollJobStatus(storedJob.job_id, onProgress);
    const filename =
      storedJob.filename ||
      `${storedJob.resource_type}-${storedJob.job_id}.zip`;

    await downloadFileFromUrl(completedJob.download_url, filename, onDownloadProgress, storedJob.job_id);
    downloadJobManager.removeJob(storedJob.job_id);

    return {
      success: true,
      downloadUrl: completedJob.download_url,
      jobId: storedJob.job_id,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`Failed to resume job ${storedJob.job_id}:`, errorMessage);

    return {
      success: false,
      error: errorMessage,
      jobId: storedJob.job_id,
    };
  }
}

// Track which jobs are currently being resumed to prevent duplicates
const resumingJobs = new Set();

/**
 * Check and resume all pending jobs on app initialization
 * Automatically downloads when jobs complete
 */
export async function resumeAllPendingJobs(
  onJobReady,
  onJobFailed,
  onPendingJobsFound
) {
  try {
    const pendingJobs = downloadJobManager.getPendingJobs();

    if (pendingJobs.length === 0) {
      console.log("No pending download jobs to resume");
      return;
    }

    console.log(
      `Found ${pendingJobs.length} pending download jobs, resuming...`
    );

    // Notify that pending jobs were found
    if (onPendingJobsFound) {
      onPendingJobsFound(pendingJobs.length);
    }

    // Resume jobs in background - don't block UI
    for (const job of pendingJobs) {
      // Skip if already being resumed
      if (resumingJobs.has(job.job_id)) {
        console.log(`Job ${job.job_id} is already being resumed, skipping duplicate`);
        continue;
      }

      // Mark as being resumed
      resumingJobs.add(job.job_id);

      // Process each job in background
      (async () => {
        try {
          // Check if already completed
          if (job.status === "completed" && job.download_url) {
            console.log(`Job ${job.job_id} already completed, triggering download`);
            const filename = job.filename || `${job.resource_type}-${job.job_id}.zip`;
            
            // Only call onJobReady callback - don't auto-download here
            // The callback should handle the download to avoid duplicates
            if (onJobReady) {
              onJobReady(job.download_url, filename, job.job_id);
            } else {
              // If no callback provided, auto-download
              try {
                await downloadFileFromUrl(job.download_url, filename, undefined, job.job_id);
                console.log(`Auto-downloaded completed job ${job.job_id}`);
              } catch (downloadError) {
                console.error(`Failed to auto-download job ${job.job_id}:`, downloadError);
              }
            }
            downloadJobManager.removeJob(job.job_id);
            resumingJobs.delete(job.job_id);
            return;
          }

          // Continue polling
          console.log(`Resuming polling for job ${job.job_id}...`);
          const completedJob = await pollJobStatus(job.job_id);
          const filename = job.filename || `${job.resource_type}-${job.job_id}.zip`;

          // Only call onJobReady callback - don't auto-download here
          // The callback should handle the download to avoid duplicates
          if (completedJob.download_url) {
            if (onJobReady) {
              onJobReady(completedJob.download_url, filename, job.job_id);
            } else {
              // If no callback provided, auto-download
              try {
                await downloadFileFromUrl(completedJob.download_url, filename, undefined, job.job_id);
                console.log(`Auto-downloaded resumed job ${job.job_id}`);
              } catch (downloadError) {
                console.error(`Failed to auto-download resumed job ${job.job_id}:`, downloadError);
              }
            }
          }

          // Clean up stored job
          downloadJobManager.removeJob(job.job_id);
          resumingJobs.delete(job.job_id);

          console.log(`Resumed job ${job.job_id} completed`);
        } catch (error) {
          console.error(`Failed to resume job ${job.job_id}:`, error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          
          if (onJobFailed) {
            onJobFailed(errorMessage, job.job_id);
          }

          // Remove from resuming set on error
          resumingJobs.delete(job.job_id);

          // Keep the job in storage for potential retry
        }
      })();
    }

    console.log("All pending jobs processing started");
  } catch (error) {
    console.error("Error resuming pending jobs:", error);
  }
}

